import { gql } from "@apollo/client";

const CERTIFICATE_FRAGMENT = gql`
  fragment CertificateFragment on IpmpAccounts {
    certificates {
      id_cert
      cert
      key
      type
      meta
      expired
    }
  }
`;

const ICON_FRAGMENT = gql`
  fragment IconFragment on IpmpAccounts {
    icon {
      description
      filename
    }
  }
`;

const EMAIL_TEMPLATES_FRAGMENT = gql`
  fragment EmailTemplatesFragment on IpmpAccounts {
    email_templates {
      description
      filename
      id_file
    }
  }
`;

const APP_CUSTOMIZATIONS_FRAGMENT = gql`
  fragment AppCustomizationsFragment on IpmpAccounts {
    app_customizations {
      id_file
    }
  }
`;

export const GET_ACCOUNT_APP_CONTENT_QUERY_NAME = "GetAccountAppContent";
export const GET_ACCOUNT_APP_CONTENT_QUERY = gql`
  query ${GET_ACCOUNT_APP_CONTENT_QUERY_NAME}($id_ipmp: Int!) {
    ipmp_account(id_ipmp: $id_ipmp) {
      app_customizations {
        files
        description
        customizations {
          name
          themes {
            brightness
            logo
            logo_type
            name
            colors {
              gradient
              primary
            }
          }
        }
      }
    }
  }
`;

export const GET_ACCOUNT_CERT_LINK_QUERY_NAME = "GetAccountCertLink";
export const GET_ACCOUNT_CERT_LINK_QUERY = gql`
  query ${GET_ACCOUNT_CERT_LINK_QUERY_NAME}($id_ipmp: Int!) {
    ipmp_account(id_ipmp: $id_ipmp) {
      certificates {
        download_url
        type
      }
    }
  }
`;

const DEFAULT_CUSTOMIZATIONS_FRAGMENT = gql`
  fragment DefaultCustomizationsFragment on IpmpAccounts {
    default_customizations {
      description
      filename
      id_file
    }
  }
`;

const NEO_XML_FRAGMENT = gql`
  fragment NeoXmlFragment on IpmpAccounts {
    neo_xml {
      xml_file
      id_xml_file
    }
  }
`;

const EVENTS_FRAGMENT = gql`
  fragment EventsFragment on IpmpAccounts {
    events {
      filename
      id_events_file
      file_type
    }
  }
`;

const IQ_CONFIG_FRAGMENT = gql`
  fragment IQConfigFragment on IpmpAccounts {
    iq_config {
      filename
      id_config
    }
  }
`;

export const IQ_PATCH_TAGS_FRAGMENT = gql`
  fragment IQPatchTagsFragment on IpmpAccounts {
    iq_patch_tags {
      id_file
      patch_tags
    }
  }
`;

export const IQ_CONFIG_SCHEMA_FRAGMENT = gql`
  fragment IQConfigSchemaFragment on IpmpAccounts {
    iq_config_schema {
      id_config
      filename
    }
  }
`;

export const GET_ACCOUNT_FILES_NAME = "GetAccountFiles";
export const GET_ACCOUNT_FILES_QUERY = gql`
  ${CERTIFICATE_FRAGMENT}
  ${ICON_FRAGMENT}
  ${EMAIL_TEMPLATES_FRAGMENT}
  ${APP_CUSTOMIZATIONS_FRAGMENT}
  ${DEFAULT_CUSTOMIZATIONS_FRAGMENT}
  ${NEO_XML_FRAGMENT}
  ${EVENTS_FRAGMENT}
  ${IQ_CONFIG_FRAGMENT}
  ${IQ_PATCH_TAGS_FRAGMENT}
  ${IQ_CONFIG_SCHEMA_FRAGMENT}
  query ${GET_ACCOUNT_FILES_NAME} ($id_ipmp: Int!) {
    ipmp_account(id_ipmp: $id_ipmp) {
      ...CertificateFragment
      ...IconFragment
      ...EmailTemplatesFragment
      ...AppCustomizationsFragment
      ...DefaultCustomizationsFragment
      ...NeoXmlFragment
      ...EventsFragment
      ...IQConfigFragment
      ...IQPatchTagsFragment
      ...IQConfigSchemaFragment
    }
  }
`;

export const UPLOAD_ACCOUNT_ANTIVIRUS_FILE = gql`
  mutation UploadAntivirusFile($file: Upload!, $id_ipmp: Int!) {
    create_antivirus_license_file(file: $file, id_ipmp: $id_ipmp) {
      success
      message
    }
  }
`;

export const REMOVE_ACCOUNT_ANTIVIRUS_FILE = gql`
  mutation RemoveAntivirusFile($ids: Int!) {
    delete_antivirus_license_file(id_ipmp: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_CERTIFICATE_FILE_NAME = "AddAccountCertificate";
export const UPLOAD_ACCOUNT_CERTIFICATE_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_CERTIFICATE_FILE_NAME}(
    $cert: Upload!
    $key: Upload!
    $id_ipmp: Int!
  ) {
    create_cert_file(
      id_ipmp: $id_ipmp
      cert: $cert
      key: $key
      cert_type: WEB
    ) {
      success
      message
    }
  }
`;

export const REMOVE_ACCOUNT_CERTIFICATE_FILE_NAME = "RemoveAccountCertificate";
export const REMOVE_ACCOUNT_CERTIFICATE_FILE = gql`
  mutation ${REMOVE_ACCOUNT_CERTIFICATE_FILE_NAME}($ids: [Int!]!) {
    delete_cert_file(ids: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_C24_CERTIFICATE_FILE_NAME =
  "UploadAccountC24Certificate";
export const UPLOAD_ACCOUNT_C24_CERTIFICATE_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_C24_CERTIFICATE_FILE_NAME}(
    $id_ipmp: Int!
    $cert: Upload!
    $key: Upload!
  ) {
    create_cert_file(
      id_ipmp: $id_ipmp
      cert: $cert
      key: $key
      cert_type: C24
    ) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_LOGFWD_CERTIFICATE_FILE_NAME =
  "UploadAccountLogFwCertificate";
export const UPLOAD_ACCOUNT_LOGFWD_CERTIFICATE_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_LOGFWD_CERTIFICATE_FILE_NAME}(
    $id_ipmp: Int!
    $ca: Upload!
    $cert: Upload!
    $key: Upload!
  ) {
    create_cert_file(
      id_ipmp: $id_ipmp
      ca: $ca
      cert: $cert
      key: $key
      cert_type: LOG_FWD
    ) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_ICON_FILE_NAME = "UploadIconFile";
export const UPLOAD_ACCOUNT_ICON_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_ICON_FILE_NAME}(
    $description: String
    $file: Upload!
    $id_ipmp: Int!
  ) {
    create_icon(file: $file, description: $description, id_ipmp: $id_ipmp) {
      success
      message
    }
  }
`;

export const RELOAD_ACCOUNT_ICON_FILE_NAME = "ReloadIconFile";
export const RELOAD_ACCOUNT_ICON_FILE = gql`
  mutation ${RELOAD_ACCOUNT_ICON_FILE_NAME}(
    $description: String
    $file: Upload!
    $id_ipmp: Int!
  ) {
    delete_icon(id_ipmp: $id_ipmp) {
      success
    }
    create_icon(file: $file, description: $description, id_ipmp: $id_ipmp) {
      success
      message
    }
  }
`;

export const REMOVE_ACCOUNT_ICON_FILE_NAME = "RemoveIconFile";
export const REMOVE_ACCOUNT_ICON_FILE = gql`
  mutation ${REMOVE_ACCOUNT_ICON_FILE_NAME}($ids: Int!) {
    delete_icon(id_ipmp: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_EMAIL_TEMPLATE_FILES_NAME =
  "UploadEmailTemplateFiles";
export const UPLOAD_ACCOUNT_EMAIL_TEMPLATE_FILES = gql`
  mutation ${UPLOAD_ACCOUNT_EMAIL_TEMPLATE_FILES_NAME}($description: String, $file: Upload!, $id_ipmp: Int!) {
    create_email_template(
      file: $file
      id_ipmp: $id_ipmp
      description: $description
    ) {
      success
      message
    }
  }
`;

export const DELETE_ACCOUNT_EMAIL_TEMPLATE_FILES_NAME =
  "RemoveEmailTemplateFiles";
export const DELETE_ACCOUNT_EMAIL_TEMPLATE_FILES = gql`
  mutation ${DELETE_ACCOUNT_EMAIL_TEMPLATE_FILES_NAME}($ids: [Int!]) {
    delete_email_template(id_file_list: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_DEFAULT_CUSTOMIZATIONS_FILE = gql`
  mutation UploadDefaultCustomizationFile(
    $description: String!
    $file: Upload!
    $id_ipmp: Int!
  ) {
    create_default_customization(
      description: $description
      file: $file
      id_ipmp: $id_ipmp
    ) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_APP_CUSTOMIZATIONS_FILE = gql`
  mutation UploadAppCustomizationFile(
    $description: String!
    $files: [Upload]!
    $id_ipmp: Int!
  ) {
    create_app_customization(
      description: $description
      files: $files
      id_ipmp: $id_ipmp
    ) {
      success
      message
    }
  }
`;

export const REMOVE_ACCOUNT_CUSTOMIZATION_FILE = gql`
  mutation RemoveCustomizationFile($ids: [Int!]!) {
    delete_customization(ids: $ids) {
      success
      message
    }
  }
`;

export const GET_ACCOUNT_ICON_CONTENT_NAME = "GetAccountIconContent";

export const GET_ACCOUNT_ICON_CONTENT_QUERY = gql`
  query ${GET_ACCOUNT_ICON_CONTENT_NAME}($id_ipmp: Int!) {
    ipmp_account(id_ipmp: $id_ipmp) {
      icon {
        content
      }
    }
  }
`;

export const UPLOAD_ACCOUNT_NEO_XML_FILE_NAME = "UploadAccountNeoXML";
export const UPLOAD_ACCOUNT_NEO_XML_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_NEO_XML_FILE_NAME}($file: Upload!, $id_ipmp: Int!) {
    create_neo_xml_file_ipmp(file: $file, id_ipmp: $id_ipmp) {
      success
      message
    }
  }
`;

export const DELETE_ACCOUNT_NEO_XML_FILE_NAME = "DeleteAccountNeoXML";
export const DELETE_ACCOUNT_NEO_XML_FILE = gql`
  mutation ${DELETE_ACCOUNT_NEO_XML_FILE_NAME}($ids: [Int!]) {
    delete_neo_xml_file_ipmp(ids: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_EVENT_FILE = gql`
  mutation CreateAccountEventFile(
    $file: Upload!
    $id_ipmp: Int!
    $type: EventTypeEnum!
  ) {
    create_event_file_ipmp(file: $file, id_ipmp: $id_ipmp, type: $type) {
      success
      message
    }
  }
`;

export const DELETE_ACCOUNT_EVENT_FILE = gql`
  mutation DeleteAccountEventFile($ids: [Int!]) {
    delete_event_file_ipmp(ids: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_IQ_CONFIG_FILE_NAME = "CreateAccountIQConfigFile";
export const UPLOAD_ACCOUNT_IQ_CONFIG_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_IQ_CONFIG_FILE_NAME}($file: Upload!, $id_ipmp: Int!) {
    create_iq_config_file_ipmp(file: $file, id_ipmp: $id_ipmp) {
      success
      message
    }
  }
`;

export const DELETE_ACCOUNT_IQ_CONFIG_FILE_NAME = "DeleteAccountIQConfigFile";
export const DELETE_ACCOUNT_IQ_CONFIG_FILE = gql`
  mutation ${DELETE_ACCOUNT_IQ_CONFIG_FILE_NAME}($ids: [Int!]!) {
    delete_iq_config_file_ipmp(ids: $ids) {
      success
      message
    }
  }
`;

export const UPLOAD_ACCOUNT_IQ_PATCH_TAG_FILE_NAME =
  "CreateAccountIQPatchTagsFile";
export const UPLOAD_ACCOUNT_IQ_PATCH_TAG_FILE = gql`
  mutation ${UPLOAD_ACCOUNT_IQ_PATCH_TAG_FILE_NAME}($id_ipmp: Int!, $file: Upload!) {
    create_iq_patch_tags_file_ipmp(file: $file, id_ipmp: $id_ipmp) {
      message
      success
    }
  }
`;

export const DELETE_ACCOUNT_IQ_PATCH_TAG_FILE_NAME =
  "DeleteAccountIQPatchTagsFile";
export const DELETE_ACCOUNT_IQ_PATCH_TAG_FILE = gql`
  mutation ${DELETE_ACCOUNT_IQ_PATCH_TAG_FILE_NAME}($ids: [Int!]!) {
    delete_iq_patch_tags_file_ipmp(ids: $ids) {
      message
      success
    }
  }
`;

export const UPDATE_ACCOUNT_IQ_PATCH_TAG_FILE_NAME =
  "UpdateAccountIQPatchTagsFile";
export const UPDATE_ACCOUNT_IQ_PATCH_TAG_FILE = gql`
  mutation ${UPDATE_ACCOUNT_IQ_PATCH_TAG_FILE_NAME}(
    $iq_patch_tags: [String]!
    $id_ipmp: Int!
  ) {
    update_iq_patch_tags_file_ipmp(
      id_ipmp: $id_ipmp
      iq_patch_tags: $iq_patch_tags
    ) {
      message
      success
    }
  }
`;

export const UPLOAD_ACCOUNT_IQ_CONFIG_SCHEMA_FILE = gql`
  mutation UploadAccountIQConfigSchemaFile($file: Upload!, $id_ipmp: Int!) {
    create_iq_config_schema(file: $file, id_ipmp: $id_ipmp) {
      success
      message
    }
  }
`;

export const DELETE_ACCOUNT_IQ_CONFIG_SCHEMA_FILE = gql`
  mutation DeleteAccountIQConfigSchemaFile($ids: [Int!]!) {
    delete_iq_config_schema(ids: $ids) {
      message
      success
    }
  }
`;

export const CREATE_ACCOUNT_APP_THEME = gql`
  mutation CreateAccountAppTheme(
    $files: [Upload]
    $id_ipmp: Int!
    $theme: GenericScalar!
  ) {
    create_app_customization_theme(
      id_ipmp: $id_ipmp
      theme: $theme
      files: $files
    ) {
      message
      success
    }
  }
`;

export const DELETE_ACCOUNT_APP_THEME = gql`
  mutation DeleteAccountAppTheme($ids: [String]!, $id_ipmp: Int!) {
    delete_app_customization_theme(id_ipmp: $id_ipmp, names: $ids) {
      message
      success
    }
  }
`;

export const UPDATE_ACCOUNT_APP_THEME = gql`
  mutation UpdateAccountAppTheme(
    $files: [Upload]
    $id_ipmp: Int!
    $theme: GenericScalar!
  ) {
    update_app_customization_theme(
      id_ipmp: $id_ipmp
      theme: $theme
      files: $files
    ) {
      message
      success
    }
  }
`;
